import * as React from 'react';
import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete } from '@mui/material';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import { TTaskAssigneeOption } from '../../types/Tasks';

interface IAssigneeChangeModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (
    assigneeEmail: string | null,
    notifyAssignee: boolean,
    comment: string
  ) => void;
  assignees: TTaskAssigneeOption[];
  currentAssigneeEmail: string | null;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
    formControl: {
      marginBottom: theme.spacing(2),
      width: '100%',
    },
  })
);

const AssigneeChangeModal: React.FC<IAssigneeChangeModalProps> = ({
  open,
  onClose,
  onApply,
  assignees,
  currentAssigneeEmail,
}): JSX.Element => {
  const classes = useStyles({});
  const { t } = useTranslation();

  const [selectedEmail, setSelectedEmail] = useState<string | null>(
    currentAssigneeEmail
  );
  const [notifyAssignee, setNotifyAssignee] = useState(true);
  const [comment, setComment] = useState('');

  const assigneeChanged = selectedEmail !== currentAssigneeEmail;

  React.useEffect(() => {
    if (open) {
      setSelectedEmail(currentAssigneeEmail);
      setNotifyAssignee(true);
      setComment('');
    }
  }, [open, currentAssigneeEmail]);

  React.useEffect(() => {
    if (!assigneeChanged) {
      setNotifyAssignee(true);
      setComment('');
    }
  }, [assigneeChanged]);

  const handleApply = () => {
    onApply(selectedEmail, notifyAssignee, comment);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <h3>{t('tasks.detail.set_assignee')}</h3>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
          size="large">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '20px' }}>
        <Autocomplete
          className={classes.formControl}
          options={assignees}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          value={
            assignees.find((a) => a.email === selectedEmail) ?? null
          }
          onChange={(_e, value) => {
            setSelectedEmail(value?.email ?? null);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('tasks.detail.assignee')}
              variant="outlined"
            />
          )}
        />

        {assigneeChanged && (
          <>
            <FormControlLabel
              className={classes.formControl}
              control={
                <Checkbox
                  checked={notifyAssignee}
                  onChange={(e) => setNotifyAssignee(e.target.checked)}
                  color="primary"
                />
              }
              label={t('tasks.detail.notify_assignee')}
            />
            <FormHelperText style={{ marginTop: -8, marginBottom: 8 }}>
              {t('tasks.detail.notify_assignee_help')}
            </FormHelperText>

            <TextField
              className={classes.formControl}
              label={t('tasks.detail.notify_assignee_comment')}
              variant="outlined"
              multiline
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              disabled={!notifyAssignee}
            />
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: '8px 24px 16px' }}>
        <Button onClick={handleApply} color="primary" variant="contained">
          {t('tasks.detail.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssigneeChangeModal;
